/**
 * صفحه‌ی دعوتِ **شخصی** — قرینه‌ی apps/mobile/app/i/[token].tsx.
 *
 * فرقش با PublicSignalPage (لینکِ عمومیِ `/s/:token`): آن یکی یک لینک برای
 * همه است و برای همین جواب نمی‌گیرد — سازنده می‌دید «یکی گفت هستم» ولی
 * نمی‌دانست کی. این یکی توکنِ خودِ آن نفر است، پس اسم را از قبل می‌داند و
 * جواب روی همان ردیفِ `signal_recipients` می‌نشیند که کاربرِ حساب‌دار هم
 * رویش می‌نشیند.
 *
 * از کجا می‌آید: لینکِ توی ایمیلِ دعوت (مایگریشن 0066 و
 * supabase/functions/send-invite-email)، و پیامکِ تک‌نفره (مایگریشن 0067).
 * ایمیل **فقط همین لینک** را دارد و هیچ دکمه‌ی جوابی توی خودش نیست — جواب
 * دادن یک جا اتفاق می‌افتد، همین‌جا، جایی که طرف زمان و مکان را می‌بیند،
 * «شاید» هم دارد، می‌تواند جوابش را عوض کند و رخداد را به تقویمش اضافه کند.
 *
 * چیدمان از `GuestSignal` می‌آید، همان بدنه‌ای که `/s/:token` هم دارد.
 * `onAnswer` تنها چیزی‌ست که این صفحه را از آن یکی جدا می‌کند.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { publicInviteRepo, type GuestResponse } from '@/core/data/inviteRepo';
import { t, useLocale } from '@/core/lib/i18n';
import { inviteUrl } from '@/core/lib/shareLink';
import { reportError } from '@/core/lib/report';
import { track, trackEventOpen, trackFunnel } from '@/core/lib/analytics';
import { Card } from '@/ui/kit';
import { GuestSignal } from './GuestSignal';

export function InvitePage() {
  useLocale();
  const { token } = useParams<{ token: string }>();
  const [error, setError] = useState<string | null>(null);

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => publicInviteRepo.byToken(token!),
    enabled: !!token,
  });

  // قیف (WHOSUP-291). این دریچه شناسه‌ی سیگنال را نمی‌دهد و توکن هم عمداً
  // ثبت نمی‌شود — کلیدِ ورودِ یک نفر است.
  const loaded = !!invite.data;
  useEffect(() => {
    if (!loaded) return;
    trackFunnel('link_entry', { kind: 'invite' });
    trackEventOpen(undefined, 'signal', 'invite_link');
  }, [loaded]);

  const respond = useMutation({
    mutationFn: (r: GuestResponse) => publicInviteRepo.respond(token!, r),
    onSuccess: (_d, r) => {
      setError(null);
      track('invite_responded', { response: r });
      trackFunnel('rsvp', { source: 'signal', response: r });
      // دوباره خوانده می‌شود چون `locked` و `my_response` هر دو سمتِ سرور
      // حساب می‌شن — حدس زدنشون این‌جا یعنی دو نسخه‌ی حقیقت.
      invite.refetch();
    },
    onError: (e) => {
      reportError('web.inviteRespond', e);
      setError(t('errorRetry'));
    },
  });

  if (invite.isLoading) return <div className="feed"><p className="dim">…</p></div>;

  // توکنِ بی‌جواب یعنی لینکِ غلط، دعوتِ پاک‌شده، یا سیگنالی که ۳۰ روز از
  // رخدادش گذشته. عمداً همون یک جمله برای هر سه: بیشتر از این گفتن، یعنی لو
  // دادنِ اینکه کدوم توکن‌ها وجود دارن. سیگنالِ **لغوشده** دیگه این‌جا
  // نمی‌افته (مایگریشن 0080) — صفحه خودش می‌گه لغو شد.
  if (!invite.data) {
    return <div className="feed"><Card><p>{t('linkExpired')}</p></Card></div>;
  }

  const s = invite.data;

  return (
    <GuestSignal
      source={s}
      pageUrl={inviteUrl(token!)}
      answering={respond.isPending}
      error={error}
      // `mutateAsync` و نه `mutate`: صفحه‌ی «ثبت شد» فقط وقتی باید بیاید که
      // جواب واقعاً نشسته باشد، و `GuestSignal` این را از همین Promise
      // می‌فهمد. ردِ Promise هم همان‌جا گرفته می‌شود.
      onAnswer={(r) => respond.mutateAsync(r)}
    />
  );
}
